import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import {
  toggleParagraphSelection,
  selectParagraphCount,
  clearDocumentSelections,
  MAX_PARAGRAPH_SELECTIONS,
} from "../../DraftSelectionSlice_Store";
import { selectActiveDocument } from "../../DocumentPreviewSlice_Store";
import { selectCurrentProjectId } from "../../ProjectSlice_Store";

const LIMIT_WARNING_MS = 5000;

export interface DraftSelection {
  projectId: string | undefined;
  docId: string | undefined;
  selectedCount: number;
  showLimitWarning: boolean;
  toggleSelection: (paragraphId: number, isCurrentlySelected: boolean) => void;
  clearSelections: () => void;
}

/** Paragraph selection for the active project/document, capped at the limit. */
export function useDraftSelection(): DraftSelection {
  const dispatch = useAppDispatch();
  const [showLimitWarning, setShowLimitWarning] = useState<boolean>(false);

  // Get current project and document
  const projectId = useAppSelector(selectCurrentProjectId);
  const activeDocument = useAppSelector(selectActiveDocument);
  const docId = activeDocument?.docId;

  // Get selected paragraph IDs from Redux
  const selectedCount = useAppSelector(selectParagraphCount(projectId, docId));

  const toggleSelection = (paragraphId: number, isCurrentlySelected: boolean): void => {
    if (!projectId || !docId) {
      console.warn("[DraftContent] Missing projectId or docId:", { projectId, docId });
      return;
    }

    // If trying to select (not deselect) and already at limit
    if (!isCurrentlySelected && selectedCount >= MAX_PARAGRAPH_SELECTIONS) {
      setShowLimitWarning(true);
      setTimeout(() => {
        setShowLimitWarning(false);
      }, LIMIT_WARNING_MS);
      return;
    }

    dispatch(toggleParagraphSelection({ projectId, docId, paragraphId: String(paragraphId) }));
  };

  const clearSelections = (): void => {
    if (projectId && docId) {
      dispatch(clearDocumentSelections({ projectId, docId }));
    }
  };

  return {
    projectId,
    docId,
    selectedCount,
    showLimitWarning,
    toggleSelection,
    clearSelections,
  };
}
